import { firstValueFrom } from 'rxjs'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { AuthenticationRepository } from 'Repository/index.js'
import Actions from '../../../Flux/index.js'
import { authenticationSlice } from '../../../Flux/Slice/Authentication/AuthenticationSlice.js'
import { addMessage } from '../../../Flux/Slice/Application/ApplicationSlice.js'
import { errorMessage, successMessage } from '../../../Model/Message/IMessage.js'
import type { OrganizationOverrideResponse } from '../../../Generated/OrganizationOverrideResponse.ts'
import type { OverrideEntitlementSourceKey } from '../../../Generated/OverrideEntitlementSource.ts'
import type { OverrideLicenseTierKey } from '../../../Generated/OverrideLicenseTier.ts'
import { OverrideLicenseTiers } from '../../../Generated/OverrideLicenseTier.ts'
import { useGetAuthenticatedUser } from 'Flux/Selector/index.js'
import { organizationOverrideOf } from 'Model/User/organizationOverride.js'
import type { BaseModalProps } from '../../Modal/BaseModal.js'
import { BaseModal } from '../../Modal/BaseModal.js'

type ViewState = 'list' | 'assumeOrganization' | 'refreshKcToken'

const TIER_KEYS = Object.keys(OverrideLicenseTiers) as OverrideLicenseTierKey[]

const SOURCE_LABEL: Record<OverrideEntitlementSourceKey, string> = {
  TARGET_ORGANIZATION: "the target organization's real licences",
  EXPLICIT_SELECTION: 'a licence level you forced',
  UNRESOLVED: 'nothing — the target organization could not be read, so licences are stripped'
}

export const MarkusAdminModal = ({ isOpen, onClose }: BaseModalProps) => {
  const dispatch = useDispatch()
  const [view, setView] = useState<ViewState>('list')
  const [organizationId, setOrganizationId] = useState('')
  const [licenseTier, setLicenseTier] = useState<OverrideLicenseTierKey | ''>('')
  const [billingMissing, setBillingMissing] = useState(false)
  const [state, setState] = useState<OrganizationOverrideResponse>()
  const [isLoading, setIsLoading] = useState(false)
  const tokenOverride = organizationOverrideOf(useGetAuthenticatedUser())

  /*
   * Prefilled from `next.override` (resolved through OrganizationOverrideResolver, so it is null once
   * expired) rather than the raw `organizationOverrideId`/`license` -- those come straight off the
   * Keycloak attributes and stay set until an explicit write, so they would keep prefilling an expired
   * override as if it were still active while the entitlements panel below already says otherwise.
   */
  const applyState = (next: OrganizationOverrideResponse) => {
    setState(next)
    setOrganizationId(next.override?.targetOrganizationId ?? '')
    setLicenseTier(next.override?.licenseOverride?.tier ?? '')
    setBillingMissing(next.override?.licenseOverride?.billingMissing ?? false)
  }

  useEffect(() => {
    if (!isOpen) {
      setView('list')
      setOrganizationId('')
      setLicenseTier('')
      setBillingMissing(false)
      setState(undefined)
      setIsLoading(false)
      return
    }

    const load = async () => {
      setIsLoading(true)
      try {
        applyState(await firstValueFrom(AuthenticationRepository.getOrganizationOverride()))
      } catch (error) {
        dispatch(addMessage(errorMessage(`Failed to load admin state: ${String(error)}`)))
      } finally {
        setIsLoading(false)
      }
    }

    void load()
  }, [dispatch, isOpen])

  /* The token-derived id keeps Clear reachable when the read above failed, which is when it is needed. */
  const currentOverrideId = state?.organizationOverrideId ?? tokenOverride?.targetOrganizationId
  const isSubmitDisabled = useMemo(() => isLoading || organizationId.trim().length === 0, [isLoading, organizationId])

  const applyOverride = async (nextOrganizationId?: string) => {
    setIsLoading(true)
    try {
      const response = await firstValueFrom(
        AuthenticationRepository.setOrganizationOverride({
          organizationId: nextOrganizationId?.trim() || undefined,
          license: nextOrganizationId && licenseTier ? { tier: licenseTier, billingMissing } : undefined
        })
      )
      dispatch(Actions[authenticationSlice.name].updateUserIfPresent())
      dispatch(
        addMessage(
          successMessage(
            response.organizationOverrideId ? 'Organization override updated' : 'Organization override cleared'
          )
        )
      )
      /* The write answers from the token it was authenticated with, so its effective figures are the
       * pre-change ones. Re-reading picks up the token the write triggered a refresh of. */
      applyState(await firstValueFrom(AuthenticationRepository.getOrganizationOverride()))
    } catch (error) {
      dispatch(addMessage(errorMessage(`Failed to update organization override: ${String(error)}`)))
    } finally {
      setIsLoading(false)
    }
  }

  const refreshKcToken = async () => {
    setIsLoading(true)
    try {
      await firstValueFrom(AuthenticationRepository.refreshKcToken())
      dispatch(Actions[authenticationSlice.name].updateUserIfPresent())
      dispatch(addMessage(successMessage('Keycloak token refresh requested')))
      onClose()
    } catch (error) {
      dispatch(addMessage(errorMessage(`Failed to refresh kc token: ${String(error)}`)))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <BaseModal isOpen={isOpen} onClose={onClose}>
      <div className="modal-box w-full max-w-2xl">
        <h3 className="text-lg font-bold">Admin</h3>

        {view === 'list' && (
          <div className="mt-4 flex flex-col gap-3">
            <button className="btn btn-outline justify-start" onClick={() => setView('assumeOrganization')}>
              Assume organization
            </button>
            <button className="btn btn-outline justify-start" onClick={() => setView('refreshKcToken')}>
              Refresh kc token
            </button>
          </div>
        )}

        {view === 'assumeOrganization' && (
          <div className="mt-4 flex flex-col gap-4">
            <button className="btn btn-ghost btn-sm w-fit px-0" onClick={() => setView('list')} disabled={isLoading}>
              Back
            </button>

            <div className="form-control">
              <label className="label">
                <span className="label-text">Entitlements in effect</span>
              </label>
              <div
                data-testid="override-entitlements"
                className="rounded border border-base-300 bg-base-200 px-3 py-2 text-sm flex flex-col gap-1"
              >
                {state ? (
                  <>
                    <span>
                      <b>{state.effective.organizationName}</b> ({state.effective.organizationId})
                    </span>
                    <span>From {state.override ? SOURCE_LABEL[state.override.entitlementSource] : 'your own organization'}</span>
                    <span>Plan: {state.effective.planId ?? 'none'}</span>
                    <span>Entitlements: {(state.effective.entitlements ?? []).join(', ') || 'none'}</span>
                    <span>Feature access: {(state.effective.featureAccess ?? []).join(', ') || 'none'}</span>
                    {state.effective.billingMissing && <span>Billing is flagged as missing</span>}
                    {state.override && (
                      <span className="opacity-70">
                        Your own organization: {state.override.operatorOrganizationName} ({state.override.operatorOrganizationId})
                      </span>
                    )}
                  </>
                ) : (
                  'Loading…'
                )}
              </div>
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text">Organization UUID</span>
              </label>
              <input
                type="text"
                className="input input-bordered w-full"
                placeholder="00000000-0000-0000-0000-000000000000"
                value={organizationId}
                onChange={(event) => setOrganizationId(event.target.value)}
                disabled={isLoading}
              />
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text">Licence level</span>
              </label>
              <select
                aria-label="Licence level"
                className="select select-bordered w-full"
                value={licenseTier}
                onChange={(event) => setLicenseTier(event.target.value as OverrideLicenseTierKey | '')}
                disabled={isLoading}
              >
                <option value="">Use the target organization&apos;s real licences</option>
                {TIER_KEYS.map((tier) => (
                  <option key={tier} value={tier}>
                    Force {tier}
                  </option>
                ))}
              </select>
              <label className="label cursor-pointer justify-start gap-2">
                <input
                  type="checkbox"
                  className="checkbox checkbox-sm"
                  checked={billingMissing}
                  onChange={(event) => setBillingMissing(event.target.checked)}
                  disabled={isLoading || !licenseTier}
                />
                <span className="label-text">Also pretend billing is missing</span>
              </label>
            </div>

            <div className="modal-action">
              <button className="btn" onClick={() => onClose()} disabled={isLoading}>
                Close
              </button>
              <button
                className="btn btn-outline"
                onClick={() => void applyOverride(undefined)}
                disabled={isLoading || !currentOverrideId}
              >
                Clear
              </button>
              <button className="btn btn-primary" onClick={() => void applyOverride(organizationId)} disabled={isSubmitDisabled}>
                Apply
              </button>
            </div>
          </div>
        )}

        {view === 'refreshKcToken' && (
          <div className="mt-4 flex flex-col gap-4">
            <button className="btn btn-ghost btn-sm w-fit px-0" onClick={() => setView('list')} disabled={isLoading}>
              Back
            </button>

            <div className="rounded border border-base-300 bg-base-200 px-3 py-3 text-sm">
              Trigger a token refresh for the current session and reload token-derived admin claims.
            </div>

            <div className="modal-action">
              <button className="btn" onClick={() => onClose()} disabled={isLoading}>
                Close
              </button>
              <button className="btn btn-primary" onClick={() => void refreshKcToken()} disabled={isLoading}>
                Refresh
              </button>
            </div>
          </div>
        )}
      </div>
    </BaseModal>
  )
}
